import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { apiGet } from '../services/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import './EstadisticasPage.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtNum(n) { return Number(n || 0).toLocaleString('es-ES'); }
function fmtPct(n) { return (Number(n || 0).toFixed(1)) + '%'; }
function today()   { return new Date().toISOString().slice(0, 10); }
function daysAgo(n) { return new Date(Date.now() - n * 86400000).toISOString().slice(0, 10); }
function fmtDay(d) {
  const [, m, day] = String(d).slice(0, 10).split('-');
  return `${day}/${m}`;
}

const PRESETS = [
  { key: '7',   label: '7 días',   from: () => daysAgo(7)  },
  { key: '30',  label: '30 días',  from: () => daysAgo(30) },
  { key: '90',  label: '90 días',  from: () => daysAgo(90) },
  { key: 'year', label: 'Este año', from: () => `${new Date().getFullYear()}-01-01` },
];

function KpiCard({ label, value, sub, color = 'var(--primary)' }) {
  return (
    <div className="est-kpi">
      <div className="est-kpi-label">{label}</div>
      <div className="est-kpi-value" style={{ color }}>{value}</div>
      {sub && <div className="est-kpi-sub">{sub}</div>}
    </div>
  );
}

// ── Página ───────────────────────────────────────────────────────────────────

export default function MetricasPage() {
  const { auth } = useContext(AuthContext);
  const token = auth.token;
  const isSuperadmin = auth.user?.role === 'superadmin';

  const [preset,     setPreset]     = useState('30');
  const [from,       setFrom]       = useState(daysAgo(30));
  const [to,         setTo]         = useState(today());
  const [tenants,    setTenants]    = useState([]);
  const [viewTenant, setViewTenant] = useState('');
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  useEffect(() => {
    if (isSuperadmin) apiGet('/tenants', token).then(setTenants).catch(() => {});
  }, [isSuperadmin, token]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const p = new URLSearchParams({ date_from: from, date_to: to });
      if (isSuperadmin && viewTenant) p.set('view_tenant_id', viewTenant);
      setData(await apiGet(`/metrics/calculadora?${p}`, token));
    } catch (e) {
      setError(e.message || 'Error cargando métricas');
      setData(null);
    } finally { setLoading(false); }
  }, [token, from, to, isSuperadmin, viewTenant]);

  useEffect(() => { load(); }, [load]);

  function applyPreset(pKey) {
    const p = PRESETS.find(x => x.key === pKey);
    if (!p) return;
    setPreset(pKey); setFrom(p.from()); setTo(today());
  }

  // Serie diaria: unir cálculos y leads por fecha (rellenando días sin datos)
  const chartData = React.useMemo(() => {
    if (!data) return [];
    const map = {};
    ((data.uniqueDaily || data.calcDaily) || []).forEach(r => {
      const d = String(r.date).slice(0, 10);
      map[d] = { ...(map[d] || {}), date: d, calcs: Number(r.count) };
    });
    (data.leadDaily || []).forEach(r => {
      const d = String(r.date).slice(0, 10);
      map[d] = { ...(map[d] || {}), date: d, leads: Number(r.count) };
    });
    // Rellenar los días del rango sin actividad (máx ~1 año)
    const out = [];
    const start = new Date(from + 'T00:00:00');
    const end   = new Date(to + 'T00:00:00');
    for (let d = new Date(start), i = 0; d <= end && i < 370; d.setDate(d.getDate() + 1), i++) {
      const key = d.toISOString().slice(0, 10);
      out.push({ date: key, label: fmtDay(key), calcs: map[key]?.calcs || 0, leads: map[key]?.leads || 0 });
    }
    return out;
  }, [data, from, to]);

  const calcs      = data?.calcs || 0;
  const uniques    = data?.uniqueCalcs ?? calcs;
  const calcLeads  = data?.calcLeads || 0;
  const noData     = Math.max(0, uniques - calcLeads);
  const conversion = uniques > 0 ? (calcLeads / uniques) * 100 : 0;

  return (
    <div className="est-page">

      {/* Filtros de periodo */}
      <div className="est-filters">
        <div className="est-presets">
          {PRESETS.map(p => (
            <button key={p.key} className={`est-preset ${preset === p.key ? 'est-preset--on' : ''}`}
              onClick={() => applyPreset(p.key)}>{p.label}</button>
          ))}
        </div>
        <div className="est-custom-dates">
          <input type="date" className="est-date-input" value={from}
            onChange={e => { setFrom(e.target.value); setPreset(''); }} />
          <span style={{ color: 'var(--muted)', fontSize: '.8rem' }}>a</span>
          <input type="date" className="est-date-input" value={to}
            onChange={e => { setTo(e.target.value); setPreset(''); }} />
        </div>
        {isSuperadmin && tenants.length > 0 && (
          <select className="est-date-input" value={viewTenant} onChange={e => setViewTenant(e.target.value)}>
            <option value="">Mi empresa</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <p style={{ color: 'var(--muted)' }}>Cargando métricas…</p>
      ) : error ? (
        <p style={{ color: '#dc2626' }}>{error}</p>
      ) : !data ? null : (
        <>
          {/* KPIs: cálculos vs datos dejados */}
          <h2 className="est-section-title">Calculadora web — personas vs leads</h2>
          <div className="est-kpi-grid">
            <KpiCard label="🧮 Personas que calcularon" value={fmtNum(uniques)}
              sub={`${fmtNum(calcs)} cálculos en total (con repeticiones)`} color="#e84300" />
            <KpiCard label="✅ Dejaron sus datos" value={fmtNum(calcLeads)}
              sub="Leads entrados por la calculadora" color="#16a34a" />
            <KpiCard label="🚪 No dejaron datos" value={fmtNum(noData)}
              sub="Calcularon precio y se fueron" color="#dc2626" />
            <KpiCard label="📈 Conversión" value={fmtPct(conversion)}
              sub="% de personas que acaban en lead" color="#3b82f6" />
          </div>

          {/* Gráfica diaria */}
          <h2 className="est-section-title">Evolución diaria</h2>
          <div className="est-card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: '1.25rem' }}>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={18} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip labelFormatter={(l, payload) => payload?.[0]?.payload?.date || l} />
                <Legend />
                <Bar dataKey="calcs" name="Personas que calcularon" fill="#e84300" radius={[3, 3, 0, 0]} />
                <Bar dataKey="leads" name="Dejaron datos (leads)" fill="#16a34a" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Leads web por formulario, para contexto */}
          <h2 className="est-section-title">Leads web por formulario (mismo periodo)</h2>
          <div className="est-kpi-grid">
            <KpiCard label="🧮 Calculadora" value={fmtNum(data.calcLeads)} color="#1d4ed8" />
            <KpiCard label="📞 Contacto" value={fmtNum(data.contactoLeads)} color="#0f766e" />
            <KpiCard label="📋 Presupuesto" value={fmtNum(data.presupuestoLeads)} color="#6d28d9" />
            <KpiCard label="🌐 Total web" value={fmtNum(data.webLeads)} color="var(--primary)" />
          </div>

          {data.firstEventAt && (
            <p style={{ color: 'var(--muted)', fontSize: '.78rem', marginTop: '1rem' }}>
              ℹ️ Los cálculos de precio se registran desde el {new Date(data.firstEventAt).toLocaleDateString('es-ES')}.
              Los periodos anteriores a esa fecha no tienen datos de cálculos (los leads sí).
              Se cuentan personas únicas: si alguien calcula varias veces, cuenta una sola vez
              (medición exacta desde el 03/08/2026 mediante identificador anónimo de visitante).
              Los registros anteriores no guardaron ningún dato del visitante, así que su cifra es una
              estimación: cálculos antiguos divididos por la media de cálculos por persona
              (medida con los datos nuevos), y nunca menos que los leads reales del periodo.
            </p>
          )}
        </>
      )}
    </div>
  );
}
